import { useApolloClient, useMutation } from '@apollo/client/react';
import { useAdvisorsPaths } from '@src/components/sco/Advisors/paths';
import type { UpsertAdvisorAgreementResult } from '@src/components/sco/Advisors/types/advisorAgreement';
import { canDraftFastForEngagement, currentAgreementId, fastAgreementHref, upsertInputFromEngagement } from '@src/components/sco/Advisors/utils/draftSeatAgreement';
import { cleanErrorMessage } from '@src/components/sco/Advisors/utils/sessionFormat';
import { UPSERT_ADVISOR_AGREEMENT } from '@src/gql/mutations/UpsertAdvisorAgreement';
import { BOARD_SEATS_ENGAGEMENTS } from '@src/gql/queries/BoardSeatsEngagements';
import { ScoAdvisorEngagement } from '@src/graphql-types';
import { useRouter } from 'next/router';
import { useCallback, useState } from 'react';

/**
 * Open the seat's current FAST, or create a linked draft (party + company prefilled).
 */
export const useDraftSeatAgreement = () => {
  const paths = useAdvisorsPaths();
  const router = useRouter();
  const apolloClient = useApolloClient();
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [upsertAdvisorAgreement, upsertMutation] = useMutation<UpsertAdvisorAgreementResult>(UPSERT_ADVISOR_AGREEMENT);
  const openOrCreate = useCallback(async (engagement: ScoAdvisorEngagement) => {
    setError(null);
    if (!canDraftFastForEngagement(engagement)) {
      setError('This seat cannot have a FAST agreement.');
      return null;
    }
    const existingId = currentAgreementId(engagement);
    if (existingId) {
      await router.push(fastAgreementHref(paths, existingId));
      return existingId;
    }
    const seatId = engagement.advisorEngagementId as string;
    setBusyId(seatId);
    try {
      const result = await upsertAdvisorAgreement({
        variables: {
          input: upsertInputFromEngagement(engagement)
        }
      });
      const agreementId = result.data?.upsertAdvisorAgreement?.advisorAgreement?.advisorAgreementId || null;
      if (!agreementId) {
        setError('Agreement was created but no id was returned.');
        return null;
      }
      await apolloClient.refetchQueries({
        include: [BOARD_SEATS_ENGAGEMENTS]
      });
      await router.push(fastAgreementHref(paths, agreementId));
      return agreementId;
    } catch (err) {
      setError(cleanErrorMessage(err, 'Could not draft a FAST agreement for this seat.'));
      return null;
    } finally {
      setBusyId(null);
    }
  }, [apolloClient, paths, router, upsertAdvisorAgreement]);
  return {
    openOrCreate,
    loading: upsertMutation.loading,
    busyId,
    error
  };
};